/**
 * The monthly status report as a 1280x720 PowerPoint-shaped slide.
 *
 * This is the ONLY surface in the app that follows the Danone QBR deck style
 * rather than TrackIT's own UI language: Danone One + Danone One Condensed,
 * the tri-colour accent bar, the highlighter pill, blue-dominant cards. That's
 * deliberate - the output gets screenshotted into decks that sit next to the
 * QBR portfolio slides, so it has to match those, not the app around it.
 *
 * It is also the editing surface. The editor renders this live beside the form
 * so there's no "generate, looks wrong, go back" loop; what the PM sees while
 * typing is exactly what lands in the deck.
 *
 * Brand rules being honoured (Danone Brand Book, Nov 2024):
 *  - Danone Blue dominates; secondary colours only where they carry meaning (p.55)
 *  - Highlighted text is always white on blue, never blue on a tint (p.60)
 *  - No gradient backgrounds; the top accent bar is the one sanctioned gradient (p.56)
 *  - Blue is shaded only via 60/30/10% tints, never invented hues (p.55-56)
 */

import { Fragment, useEffect, useLayoutEffect, useRef, useState } from 'react'
import type { ProjectReport, RagStatus, Milestone, ReportBullet, ReportRisk } from '../types'
import { PROJECT_STAGES } from '../types'
import { allocateListTrim, effectiveRag, layoutTimeline, shortMonthLabel } from '../lib/report'
import { renderRich, parseBlocks } from '../lib/richText'
import { formatDate } from '../lib/utils'

/**
 * What the lists column reported after fitting itself: how many items in each
 * section did not make it, and whether there is room left to bring the Open
 * Risks card back. The editor turns this into warnings and disables the risks
 * toggle when the answer is no.
 */
export interface ListsSpaceInfo {
  hidden: { highlights: number; lowlights: number; risks: number; supportNeeded: number }
  hiddenTotal: number
  /** Unused height (px, slide scale) left in the lists column once fitted. */
  freePx: number
  roomForRisks: boolean
}

/** Smallest useful Open Risks card: header, one risk row, paddings and the
 *  column gap. Below this free space the card cannot come back. Doubles as
 *  the cost of any new card, since they are structurally identical. */
export const RISKS_CARD_MIN_PX = 68

/** Space one more bullet row needs in an existing card. The editor disables
 *  its Add buttons below this, so filling stops at the budget instead of
 *  knocking lower sections off the slide. */
export const LIST_ROW_MIN_PX = 24

export const SLIDE_W = 1280
export const SLIDE_H = 720

// Brand-book palette. The tri-colour accent bar keeps the QBR deck's Pantone
// 361 green (a fixed brand device); everything that MEANS "green status" uses
// Pantone 348 (#00843D) - the same green as the rest of TrackIT - so RAG
// reads stay consistent between the app and the slide.
const BLUE = '#005EB8'
const CYAN = '#00ACED'
const BAR_GREEN = '#54AD18'
const GREEN = '#00843D'
const ORANGE = '#F88806'
const RED = '#EF3340'

const RAG_HEX: Record<RagStatus, string> = { green: GREEN, amber: ORANGE, red: RED }
const RAG_WORD: Record<RagStatus, string> = { green: 'Green', amber: 'Amber', red: 'Red' }

const fmtMoney = (v: number): string => {
  if (v >= 1_000_000) return `€${(v / 1_000_000).toFixed(1)}`
  if (v >= 1_000) return `€${(v / 1_000).toFixed(0)}`
  return `€${v.toFixed(0)}`
}
const moneyUnit = (v: number): string => (v >= 1_000_000 ? 'm' : v >= 1_000 ? 'k' : '')

/** Eyebrow + 2px rule, the house section header. */
function CardHead({
  title, icon, accent = BLUE, right,
}: { title: string; icon: React.ReactNode; accent?: string; right?: React.ReactNode }) {
  return (
    <div className="px-3 py-[5px] flex items-center justify-between" style={{ borderBottom: `2px solid ${accent}` }}>
      <div className="flex items-center gap-1.5">
        {icon}
        <div className="font-cond text-[10px] tracking-[2px] uppercase font-bold" style={{ color: accent }}>{title}</div>
      </div>
      {right}
    </div>
  )
}

const Card = ({ children, borderColor, className = '' }: {
  children: React.ReactNode; borderColor?: string; className?: string
}) => (
  <div
    className={`rounded-xl bg-white overflow-hidden ${className}`}
    style={{ border: `2px solid ${borderColor ?? 'rgba(0,94,184,0.10)'}` }}
  >
    {children}
  </div>
)

const Ico = ({ d, stroke = BLUE, size = 11 }: { d: React.ReactNode; stroke?: string; size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={stroke}
       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{d}</svg>
)

const I = {
  people: <><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.87" /></>,
  pulse: <path d="M22 12h-4l-3 9L9 3l-3 9H2" />,
  euro: <><path d="M4 10h12M4 14h9" /><path d="M19 6a7.7 7.7 0 0 0-5.2-2A7.9 7.9 0 0 0 6 12c0 4.4 3.5 8 7.8 8 2 0 3.8-.8 5.2-2" /></>,
  doc: <><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6M8 13h8M8 17h5" /></>,
  flag: <path d="M4 22V4h14l-3 5 3 5H4" />,
  warn: <><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><path d="M12 9v4M12 17h.01" /></>,
  bulb: <path d="M9 18h6M10 22h4M12 2a7 7 0 0 0-4 12.7V17h8v-2.3A7 7 0 0 0 12 2z" />,
  shield: <><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /><path d="m9 12 2 2 4-4" /></>,
  cal: <><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M3 10h18M8 3v4M16 3v4" /></>,
  arrow: <><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></>,
}

function StageRibbon({ stage }: { stage?: string }) {
  const idx = PROJECT_STAGES.findIndex(s => s.key === stage)
  return (
    <div className="flex gap-[3px]">
      {PROJECT_STAGES.map((s, i) => {
        const done = idx > -1 && i < idx
        const curr = i === idx
        // Blue is shaded by tint only - 35% for completed, solid for current,
        // 10% for not-yet-reached. Never a different hue.
        const bg = curr ? BLUE : done ? 'rgba(0,94,184,0.35)' : 'rgba(0,94,184,0.10)'
        const fg = curr || done ? '#fff' : 'rgba(0,94,184,0.55)'
        const clip = i === 0
          ? 'polygon(0 0, calc(100% - 10px) 0, 100% 50%, calc(100% - 10px) 100%, 0 100%)'
          : i === PROJECT_STAGES.length - 1
            ? 'polygon(0 0, 100% 0, 100% 100%, 0 100%, 10px 50%)'
            : 'polygon(0 0, calc(100% - 10px) 0, 100% 50%, calc(100% - 10px) 100%, 0 100%, 10px 50%)'
        return (
          <div key={s.key}
               className={`flex-1 flex items-center justify-center gap-1.5 py-[5px] px-3 font-cond font-bold uppercase ${curr ? 'text-[10px] tracking-[1.5px]' : 'text-[9.5px] tracking-[1.2px]'}`}
               style={{ background: bg, color: fg, clipPath: clip }}>
            {done && <Ico size={9} stroke="#fff" d={<polyline points="20 6 9 17 4 12" />} />}
            {s.label}
          </div>
        )
      })}
    </div>
  )
}

/**
 * One status tile: label + coloured dot in a bordered box, matching the
 * reference deck's row of RAG squares. No words beyond the label - the
 * reasons, provenance and override trail live in the editor and the stored
 * report, so the slide keeps its at-a-glance read.
 *
 * `rag: null` renders a dashed ring, used for Previous on a first report.
 */
function StatusTile({ label, rag, emphasis, muted }: {
  label: string; rag: RagStatus | null; emphasis?: boolean; muted?: boolean
}) {
  return (
    <div className="rounded-lg flex flex-col items-center justify-center gap-[5px] py-[7px]"
         style={{
           border: `1px solid rgba(0,94,184,${emphasis ? 0.3 : 0.15})`,
           background: emphasis ? 'rgba(0,94,184,0.04)' : undefined,
         }}>
      <div className={`font-cond text-[8.5px] tracking-[1px] uppercase font-bold ${muted ? 'text-gray-400' : 'text-gray-500'}`}>
        {label}
      </div>
      {rag
        ? <span className="rounded-full" style={{
            width: muted ? 13 : 17, height: muted ? 13 : 17,
            background: RAG_HEX[rag], opacity: muted ? 0.55 : 1,
          }} />
        : <span className="rounded-full" style={{ width: 13, height: 13, border: '2px dashed #cbd5e1' }} />}
    </div>
  )
}

const RISK_HEX: Record<'green' | 'amber' | 'red', string> = { green: GREEN, amber: ORANGE, red: RED }
const RISK_WORD: Record<'green' | 'amber' | 'red', string> = { green: 'Green', amber: 'Amber', red: 'Red' }

/**
 * Open risks straight from the project's register, worst first.
 *
 * A coloured dot per risk. The register's 1-16 rating orders the list but is
 * not shown - the number means nothing without the impact x likelihood matrix
 * in front of the reader.
 *
 * The band is carried in the dot's title so it is still reachable on hover and
 * to a screen reader, even though the slide itself shows colour alone.
 *
 * Capped so the card cannot push the slide past 720px; anything beyond the cap
 * is counted in the header rather than dropped silently.
 */
function RiskList({ risks, hidden }: { risks: ReportRisk[]; hidden: number }) {
  if (risks.length === 0 && hidden === 0) {
    return <div className="text-[10.5px] text-gray-400 italic">No open risks logged</div>
  }
  return (
    <div className="flex flex-col gap-[4px]">
      {risks.map(r => (
        <div key={r.id} className="flex items-baseline gap-2">
          <span className="w-[7px] h-[7px] rounded-full shrink-0 self-start mt-[5px]"
                style={{ background: RISK_HEX[r.rag] }}
                title={`${RISK_WORD[r.rag]} risk`} />
          <div className="text-[11px] text-gray-700 leading-[1.25] flex-1 min-w-0 truncate">{r.title}</div>
          {r.owner && (
            <div className="text-[9.5px] text-gray-400 shrink-0 max-w-[90px] truncate">{r.owner}</div>
          )}
        </div>
      ))}
      {hidden > 0 && (
        <div className="text-[9.5px] text-gray-400 italic">+{hidden} more in the register</div>
      )}
    </div>
  )
}

function BulletList({
  items, colour, empty,
}: { items: ReportBullet[]; colour: string; empty: string }) {
  if (items.length === 0) return <div className="text-[10.5px] text-gray-400 italic">{empty}</div>
  return (
    <div className="flex flex-col gap-[4px]">
      {items.map(b => (
        <div key={b.id} className="flex gap-2">
          <span className="w-[7px] h-[7px] rounded-full shrink-0 mt-[5px]" style={{ background: colour }} />
          <div className="text-[11px] text-gray-700 leading-[1.25]">
            {renderRich(b.text, `bl${b.id}`)}
            {b.riskId && (
              <span className="font-cond text-[8px] tracking-[1px] uppercase font-bold px-[4px] py-[1px] rounded ml-1.5"
                    style={{ background: 'rgba(0,94,184,0.10)', color: BLUE, verticalAlign: '1px' }}>
                Risk Log
              </span>
            )}
          </div>
        </div>
      ))}
    </div>
  )
}

// Achieved is solid blue - banked, and the strongest mark on the track.
// Planned rings in green to read as "on track for this", which is what a
// planned milestone with no risk against it means. Amber and red are the
// PM's explicit calls that it is slipping or gone.
const MILESTONE_STYLE: Record<Milestone['status'],
  { fill: string; stroke?: string; strokeOpacity?: number; tick: boolean }> = {
  achieved: { fill: BLUE, tick: true },
  at_risk: { fill: '#ffffff', stroke: ORANGE, tick: false },
  missed: { fill: '#ffffff', stroke: RED, tick: false },
  // Held back to 55% so "on track" reads as reassurance rather than competing
  // with the amber and red rings, which are the ones that need to be noticed.
  planned: { fill: '#ffffff', stroke: GREEN, strokeOpacity: 0.55, tick: false },
}

function Timeline({ milestones, reportDate }: { milestones: Milestone[]; reportDate?: string }) {
  const VB_W = 1180
  const PAD = 26
  const TRACK = VB_W - PAD * 2
  // Widths are fed from this component's own geometry so the row packing
  // reserves the space the text actually takes at 10.5px.
  const layout = layoutTimeline(milestones, {
    reportDate,
    charWidth: 6 / TRACK,
    labelGap: 8 / TRACK,
  })


  if (layout.points.length === 0) {
    return (
      <div className="px-3 py-4 text-[10.5px] text-gray-400 italic">
        No milestones yet. Add them in the Milestones tab to build the timeline.
      </div>
    )
  }

  // Label rows stack upward from the track. Each row is only as tall as its
  // tallest label, so one wrapped two-line label doesn't force that height on
  // every other row.
  const LINE_H = 12
  const ROW_PAD = 6
  // An SVG y is the text BASELINE, so the topmost line reaches ASCENT above
  // its own y. Without this allowance the highest row was drawn a couple of
  // pixels above the viewBox and clipped by the card, even with space to
  // spare underneath. Covers the cap height at 10.5px plus the white halo.
  const ASCENT = 10
  const rowHeights = Array.from({ length: layout.rows }, (_, r) => layout.rowLines[r] ?? 1)
  /** Text lines sitting between the track and the bottom of the given row. */
  const linesBelow = (row: number) =>
    rowHeights.slice(0, row).reduce((sum, h) => sum + h, 0)
  const totalLines = rowHeights.reduce((sum, h) => sum + h, 0)

  const labelBlock = ASCENT + totalLines * LINE_H
  const trackY = labelBlock + 12
  const vbH = trackY + 42
  const px = (x: number) => PAD + x * TRACK
  /** Baseline of the LAST line of a row - the one nearest the track. */
  const rowBaseline = (row: number) => labelBlock - linesBelow(row) * LINE_H - ROW_PAD

  return (
    <div className="px-2 pt-1 pb-[2px]">
      <svg viewBox={`0 0 ${VB_W} ${vbH}`} style={{ display: 'block', width: '100%', height: vbH }}>
          {layout.points.map(p => {
            const late = p.milestone.status === 'at_risk' || p.milestone.status === 'missed'
            const baseline = rowBaseline(p.row)
            // Lines are drawn bottom-up so the last one always sits closest to
            // the track, whatever a neighbour in the same row wrapped to.
            const firstLineY = baseline - (p.lines.length - 1) * LINE_H
            return (
              <g key={p.milestone.id} data-row={p.row}>
                {/* Leader runs from the label to the dot, so a label nudged in
                    from the edge still points at its true date. */}
                <line x1={px(p.labelX)} y1={baseline + 4} x2={px(p.x)} y2={trackY - 8}
                      stroke={BLUE} strokeOpacity={0.25} strokeWidth={1} />
                <text x={px(p.labelX)} y={firstLineY} textAnchor="middle"
                      fontFamily="'Danone One', sans-serif" fontSize={10.5} fontWeight={700}
                      fill={late ? '#B8640B' : '#374151'}
                      /* White halo: if anything does end up crossing, the text
                         stays readable rather than turning to mush. */
                      stroke="#ffffff" strokeWidth={2.5} paintOrder="stroke"
                      strokeLinejoin="round">
                  {p.lines.map((line, i) => (
                    <tspan key={i} x={px(p.labelX)} dy={i === 0 ? 0 : LINE_H}>{line}</tspan>
                  ))}
                </text>
              </g>
            )
          })}

          {/* Track: solid up to the report date, dashed after - past vs future
              reads instantly without a legend. */}
          {layout.todayX !== null ? (
            <>
              <line x1={PAD} y1={trackY} x2={px(layout.todayX)} y2={trackY} stroke={BLUE} strokeWidth={2.5} strokeLinecap="round" />
              <line x1={px(layout.todayX)} y1={trackY} x2={VB_W - PAD} y2={trackY} stroke={BLUE} strokeOpacity={0.22} strokeWidth={2.5} strokeDasharray="5 5" strokeLinecap="round" />
              <line x1={px(layout.todayX)} y1={trackY - 16} x2={px(layout.todayX)} y2={trackY + 16} stroke={CYAN} strokeWidth={1.5} strokeDasharray="3 3" />
              <text x={px(layout.todayX) + 6} y={trackY - 9} fontFamily="'Danone One Condensed', sans-serif"
                    fontSize={9} fontWeight={700} fill={CYAN} letterSpacing={1}
                    stroke="#ffffff" strokeWidth={2.5} paintOrder="stroke" strokeLinejoin="round">
                {reportDate ? formatDate(reportDate).toUpperCase().slice(0, 6) : ''}
              </text>
            </>
          ) : (
            <line x1={PAD} y1={trackY} x2={VB_W - PAD} y2={trackY} stroke={BLUE} strokeWidth={2.5} strokeLinecap="round" />
          )}

          {layout.points.map(p => {
            const st = MILESTONE_STYLE[p.milestone.status]
            const cx = px(p.x)
            return (
              <g key={`dot-${p.milestone.id}`}>
                <circle cx={cx} cy={trackY} r={st.stroke ? 8 : 7.5} fill={st.fill}
                        stroke={st.stroke} strokeOpacity={st.strokeOpacity ?? 1}
                        strokeWidth={st.stroke ? 3 : 0} />
                {st.tick && (
                  <path d={`M${cx - 3.5} ${trackY} l2.4 2.4 l4.6 -5`} stroke="#fff" strokeWidth={1.9}
                        fill="none" strokeLinecap="round" strokeLinejoin="round" />
                )}
              </g>
            )
          })}

          {layout.ticks.map(t => (
            <text key={t.label} x={px(t.x)} y={trackY + 30} textAnchor="middle"
                  fontFamily="'Danone One Condensed', sans-serif" fontSize={9.5} fontWeight={700}
                  fill="#9ca3af" letterSpacing={1}>
              {t.label}
            </text>
          ))}
      </svg>
    </div>
  )
}

interface ReportSlideProps {
  report: ProjectReport
  /**
   * Fires whenever the summary text does / stops fitting its card. The editor
   * uses it to warn the PM that their narrative will clip, since the slide
   * itself silently crops (a deck can't scroll).
   */
  onSummaryOverflow?: (clipped: boolean) => void
  /** Fires once the lists column has fitted itself; see ListsSpaceInfo. */
  onListsOverflow?: (info: ListsSpaceInfo) => void
  /** Test-only: pins the trim count instead of measuring. jsdom reports zero
   *  heights, so the measuring loop cannot run there. */
  forceTrim?: number
}

export default function ReportSlide({ report, onSummaryOverflow, onListsOverflow, forceTrim }: ReportSlideProps) {
  const s = report.snapshot
  const overall = report.overall
  const prev = report.overallPrevious

  // Direction of travel, not just "changed": a move from Red to Amber is
  // recovery and a move the other way is escalation, and leadership reads
  // those very differently.
  const RANK: Record<RagStatus, number> = { green: 0, amber: 1, red: 2 }
  const trendLabel = !prev
    ? { text: 'First report', colour: '#9ca3af' }
    : prev === overall
      ? { text: 'No change', colour: '#9ca3af' }
      : RANK[overall] < RANK[prev]
        ? { text: `Improved from ${RAG_WORD[prev]}`, colour: GREEN }
        : { text: `Down from ${RAG_WORD[prev]}`, colour: RED }

  const achieved = s.milestones.filter(m => m.status === 'achieved').length
  const atRisk = s.milestones.filter(m => m.status === 'at_risk' || m.status === 'missed').length

  // Blank bullets (a row the PM added but hasn't typed into yet) never render;
  // counts use the same filtered lists so the header numbers can't disagree.
  const notBlank = (b: ReportBullet) => b.text.trim() !== ''
  const highlights = report.highlights.filter(notBlank)
  const lowlights = report.lowlights.filter(notBlank)
  const asks = report.supportNeeded.filter(notBlank)

  // Hidden means the block is dropped entirely rather than shown empty - a
  // board pack that covers risk elsewhere should not carry a stub here.
  const risks = report.hideRisks ? [] : (s.risks ?? [])

  /**
   * Fit the lists column by measurement, exactly like the summary's type
   * ladder: render, measure, and while the column overflows drop one more item
   * (bottom-up: asks, then risks, lowlights, highlights - reverse slide order,
   * so the column fills first-come-first-served from the top). Whole items
   * only; a half-clipped row is what used to make the column look mixed up.
   *
   * There is deliberately no per-section cap. A report that is all highlights
   * is allowed to be; it just leaves no room for what sits below, and the
   * editor is told exactly what did not fit.
   */
  const listsRef = useRef<HTMLDivElement>(null)
  const [autoTrim, setAutoTrim] = useState(0)
  // Bumped whenever the space itself moves - the column resizing (a taller or
  // shorter milestone timeline changes what is left for the body) or the web
  // font arriving after first paint and reflowing every line. The trim was
  // originally recomputed only when list CONTENT changed, so freed space
  // never brought trimmed items back: delete four milestones and the asks
  // stayed hidden with the column visibly half empty.
  const [fitEpoch, setFitEpoch] = useState(0)
  const trim = forceTrim ?? autoTrim

  useEffect(() => {
    const col = listsRef.current
    if (!col) return
    let alive = true
    let raf = 0
    const bump = () => {
      cancelAnimationFrame(raf)
      raf = requestAnimationFrame(() => { if (alive) setFitEpoch(e => e + 1) })
    }
    // Guarded: jsdom (and nothing else we support) lacks ResizeObserver, and
    // the editor's ProjectReport page already polyfills nothing for it.
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(bump) : null
    ro?.observe(col)
    document.fonts?.ready.then(() => { if (alive) setFitEpoch(e => e + 1) }).catch(() => {})
    return () => { alive = false; cancelAnimationFrame(raf); ro?.disconnect() }
  }, [])
  const listCounts = {
    highlights: highlights.length, lowlights: lowlights.length,
    risks: risks.length, supportNeeded: asks.length,
  }
  const totalListItems = highlights.length + lowlights.length + risks.length + asks.length
  const listsKey = JSON.stringify([
    highlights.map(b => b.text), lowlights.map(b => b.text), asks.map(b => b.text),
    risks.map(r => [r.title, r.owner]), report.hideRisks,
  ])
  const visibleCounts = allocateListTrim(listCounts, trim)
  const hiddenCounts = {
    highlights: highlights.length - visibleCounts.highlights,
    lowlights: lowlights.length - visibleCounts.lowlights,
    risks: risks.length - visibleCounts.risks,
    supportNeeded: asks.length - visibleCounts.supportNeeded,
  }

  // Re-climb from zero on any change of content or space. Growing only, so
  // the fit can never oscillate between trimming and untrimming.
  useLayoutEffect(() => { setAutoTrim(0) }, [listsKey, fitEpoch])

  useLayoutEffect(() => {
    if (forceTrim != null) return
    const col = listsRef.current
    if (!col) return
    const over = col.scrollHeight > col.clientHeight + 1
    if (over && autoTrim < totalListItems) { setAutoTrim(v => v + 1); return }
    if (!onListsOverflow) return
    // Free space, measured in layout coordinates (offsetTop/offsetHeight)
    // rather than getBoundingClientRect - the preview renders inside a CSS
    // scale transform, which shrinks rects but not layout metrics, and mixing
    // the two would misreport the space by the zoom factor.
    const kids = [...col.children] as HTMLElement[]
    const used = kids.length
      ? Math.max(...kids.map(k => k.offsetTop + k.offsetHeight)) - kids[0].offsetTop
      : 0
    const freePx = Math.max(0, col.clientHeight - used)
    // Full is full: enabling risks never displaces something already on the
    // slide. The editor blocks additions when the space is spent and tells the
    // PM to remove or shorten what they have used.
    onListsOverflow({
      hidden: hiddenCounts,
      hiddenTotal: hiddenCounts.highlights + hiddenCounts.lowlights + hiddenCounts.risks + hiddenCounts.supportNeeded,
      freePx,
      roomForRisks: freePx >= RISKS_CARD_MIN_PX,
    })
    // hiddenCounts is derived from the deps above; listing it would re-run the
    // effect on every render for no change in inputs.
  }, [listsKey, fitEpoch, autoTrim, totalListItems, forceTrim, onListsOverflow])

  const blocks = parseBlocks(report.narrative)

  /**
   * Auto-fit the summary rather than offering a font-size control.
   *
   * Sizes both ways. A short narrative used to sit at 11px marooned in a large
   * empty card; a long one clipped. The card now takes the largest step whose
   * text still fits, so the box is filled either way and the PM never thinks
   * about type size.
   *
   * A manual picker was the obvious alternative and is worse: it invites
   * cramming, and a deck whose twelve project slides each use a different size
   * reads as amateur. Automatic sizing keeps every slide consistent for the
   * amount of text it actually carries.
   *
   * The ladder stops at 10.5px because the QBR style guide puts the floor
   * there and says to restructure rather than shrink past it. Still too long at
   * the floor and the editor is told, so the PM is asked to cut.
   */
  const summaryRef = useRef<HTMLDivElement>(null)
  const [step, setStep] = useState(0)
  const contentKey = `${report.whyLine}\u0000${report.narrative}`

  // Largest first; the fitting pass walks down until the text clears.
  const SUMMARY_STEPS = [
    { why: 'text-[14px] leading-[1.3]',    body: 'text-[13.5px] leading-[1.35]', gap: 'mb-[8px]' },
    { why: 'text-[13px] leading-[1.3]',    body: 'text-[12.5px] leading-[1.32]', gap: 'mb-[7px]' },
    { why: 'text-[12px] leading-[1.3]',    body: 'text-[11.5px] leading-[1.3]',  gap: 'mb-[6px]' },
    { why: 'text-[11.5px] leading-[1.3]',  body: 'text-[11px] leading-[1.3]',    gap: 'mb-[6px]' },
    { why: 'text-[11px] leading-[1.25]',   body: 'text-[10.5px] leading-[1.22]', gap: 'mb-[5px]' },
  ]
  const LAST = SUMMARY_STEPS.length - 1

  // Restart from the largest whenever the text changes, so shortening it grows
  // the type back rather than leaving it stuck at whatever fitted before.
  useLayoutEffect(() => { setStep(0) }, [contentKey])

  useLayoutEffect(() => {
    const el = summaryRef.current
    if (!el) return
    const over = el.scrollHeight > el.clientHeight + 2
    if (over && step < LAST) { setStep(s => s + 1); return }
    onSummaryOverflow?.(over)
  }, [contentKey, step, LAST, onSummaryOverflow])

  const fit = SUMMARY_STEPS[step]

  // Multi-year programmes are usually committed to a month, not a day, and
  // "Sep 2025 → Dec 2027" promises less than "2 Sep 2025 → 22 Dec 2027".
  const fmt = (iso: string) => {
    if (report.datePrecision === 'tbc') return 'TBC'
    // Read from the calendar parts: `new Date` takes a date-only string as UTC
    // midnight, which west of UTC is the day before, so 1 October read as Q3.
    const [year, month] = iso.slice(0, 7).split('-').map(Number)
    if (report.datePrecision === 'quarter') {
      return `Q${Math.ceil(month / 3)} ${year}`
    }
    if (report.datePrecision === 'month') {
      return new Date(year, month - 1, 1).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })
    }
    return formatDate(iso)
  }

  const windowLabel = s.startDate || s.endDate
    ? `${s.startDate ? fmt(s.startDate) : '…'} → ${s.endDate ? fmt(s.endDate) : '…'}`
    : null

  // Two short columns rather than one list: as five stacked rows Governance
  // was the tallest card in its band and stretched Status and Budget with it.
  const govColumns: { labelW: number; rows: { k: string; v: string | null }[] }[] = [
    {
      labelW: 30,
      rows: [
        { k: 'VP', v: s.functionalVP || null },
        { k: 'Lead', v: s.functionalLead || null },
        { k: 'PM', v: s.projectManager || null },
      ],
    },
    {
      labelW: 48,
      rows: [
        { k: 'Window', v: windowLabel },
        { k: 'Go-Live', v: s.goLive ? fmt(s.goLive) : null },
      ],
    },
  ]

  const budgetBar = (alloc: number, committed?: number) =>
    alloc > 0 && committed != null ? Math.min(100, Math.round((committed / alloc) * 100)) : 0

  return (
    // Flex column so the slide always fills 720px: the narrative row absorbs
    // the slack and the milestone timeline stays pinned to the bottom edge.
    // Without this a short narrative collapses everything upward and leaves a
    // block of dead white space where the deck expects content.
    <div className="relative bg-white overflow-hidden flex flex-col"
         style={{ width: SLIDE_W, height: SLIDE_H, border: '1px solid #000' }}>

      {/* Tri-colour accent bar - the one sanctioned gradient in the brand book */}
      <div className="absolute top-0 left-0 right-0 h-[6px]"
           style={{ background: `linear-gradient(90deg, ${BLUE} 0%, ${BLUE} 40%, ${CYAN} 40%, ${CYAN} 70%, ${BAR_GREEN} 70%, ${BAR_GREEN} 100%)` }} />

      {/* Header */}
      <div className="px-8 pt-4 pb-2 flex items-end justify-between shrink-0">
        <div className="min-w-0">
          <h1 className="text-[22px] font-bold leading-[0.95] tracking-[-0.5px]" style={{ color: BLUE }}>
            {s.projectName}{' '}
            <span style={{ background: BLUE, color: 'white', padding: '1px 10px 6px', borderRadius: 4, display: 'inline-block', lineHeight: 1, verticalAlign: 'baseline' }}>
              {shortMonthLabel(report.month)}
            </span>
          </h1>
        </div>
        <div className="flex items-center gap-3 shrink-0 pb-[2px]">
          {(['green', 'amber', 'red'] as RagStatus[]).map(r => (
            <div key={r} className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: RAG_HEX[r] }} />
              <span className="font-cond text-[9.5px] font-bold text-gray-600 uppercase tracking-[1.5px]">
                {r === 'green' ? 'On Track' : r === 'amber' ? 'At Risk' : 'Off Track'}
              </span>
            </div>
          ))}
        </div>
      </div>

      <div className="px-8 pb-2 shrink-0"><StageRibbon stage={s.stage} /></div>

      {/* Governance | Status | Budget. Widths follow content: Status was
          previously 1fr and ended up 722px wide holding six small tiles, which
          read as dead space. Governance takes the slack instead - it holds
          names and dates side by side. The band is as tall as its tallest
          card, so each is kept to about three lines; the height saved goes to
          the summary and lists below. */}
      <div className="px-8 pb-2 grid gap-2 shrink-0" style={{ gridTemplateColumns: '1fr 500px 250px' }}>
        <Card>
          <CardHead title="Governance" icon={<Ico d={I.people} />} />
          {/* People take the flexible column and truncate; dates are sized to
              their content, since a clipped date is worse than a clipped name. */}
          <div className="px-3 py-[6px] grid gap-x-4" style={{ gridTemplateColumns: 'minmax(0, 1fr) auto' }}>
            {govColumns.map(({ labelW, rows }) => (
              <div key={rows[0].k} className="flex flex-col gap-[3px] min-w-0">
                {rows.map(({ k, v }) => (
                  <div key={k} className="flex items-baseline min-w-0">
                    <div className="font-cond text-[9px] tracking-[1px] uppercase text-gray-500 font-bold shrink-0"
                         style={{ width: labelW }}>{k}</div>
                    {v
                      ? <div className="text-[11px] leading-[1.3] text-gray-700 font-bold truncate">{v}</div>
                      : <div className="text-[11px] leading-[1.3] text-gray-400 italic">Not set</div>}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </Card>

        <Card className="flex flex-col">
          <CardHead title="Status" icon={<Ico d={I.pulse} />}
            right={
              <div className="font-cond text-[9px] tracking-[1px] uppercase font-bold"
                   style={{ color: trendLabel.colour }}>
                {trendLabel.text}
              </div>
            } />
          {/* Six tiles in one row, as on the reference deck. Previous sits
              beside Current because that comparison is the point of a monthly
              report, and it fills width that four dots alone left empty. */}
          <div className="px-2 py-[6px] flex-1 grid grid-cols-6 gap-1.5">
            <StatusTile label="Previous" rag={prev ?? null} muted />
            <StatusTile label="Current" rag={overall} emphasis />
            <StatusTile label="Scope" rag={effectiveRag(report.scope)} />
            <StatusTile label="Schedule" rag={effectiveRag(report.schedule)} />
            <StatusTile label="Cost" rag={effectiveRag(report.cost)} />
            <StatusTile label="Benefit" rag={effectiveRag(report.benefit)} />
          </div>
        </Card>

        <Card>
          <CardHead title="Total Budget" icon={<Ico d={I.euro} />} />
          <div className="px-3 py-[6px] flex gap-4">
            {([['Capex', s.capex, s.capexCommitted, RED], ['Opex', s.opex, s.opexCommitted, BLUE]] as const).map(([label, alloc, committed, barColour]) => (
              <div key={label} className="flex-1 min-w-0">
                <div className="font-cond text-[9px] tracking-[1px] uppercase text-gray-500 font-bold leading-none mb-[2px]">{label}</div>
                <div className="text-[20px] font-bold leading-[1.05]" style={{ color: BLUE, fontFeatureSettings: '"tnum"' }}>
                  {fmtMoney(alloc)}<span className="text-[11px] text-gray-500">{moneyUnit(alloc)}</span>
                </div>
                <div className="h-[4px] rounded-full mt-[3px] overflow-hidden" style={{ background: 'rgba(0,94,184,0.10)' }}>
                  <div className="h-full rounded-full" style={{ width: `${budgetBar(alloc, committed)}%`, background: barColour }} />
                </div>
                <div className="text-[8.5px] text-gray-500 mt-[2px] leading-[1.2]">
                  {committed != null
                    ? `${fmtMoney(committed)}${moneyUnit(committed)} committed`
                    : alloc > 0 ? 'Allocation agreed' : 'No allocation'}
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Narrative | lists - absorbs the vertical slack */}
      {/* gridTemplateRows minmax(0,1fr) pins the row to the space the slide
          actually has. Without it the implicit auto row grows with content, so
          an overstuffed column never overflowed itself - it silently grew past
          the 720px edge and took the summary card with it, which is what made
          a full column look mixed up. With the row pinned, the column really
          overflows internally and the trim loop can measure it. */}
      <div className="px-8 pb-2 grid gap-2 flex-1 min-h-0"
           style={{ gridTemplateColumns: '1.3fr 1fr', gridTemplateRows: 'minmax(0, 1fr)' }}>
        <Card className="flex flex-col min-h-0">
          <CardHead title="Overall Summary" icon={<Ico d={I.doc} />} />
          <div ref={summaryRef} className="px-3 py-2 overflow-hidden flex-1 min-h-0">
            {report.whyLine.trim() && (
              <div className="rounded-md overflow-hidden mb-[7px]"
                   style={{ background: `${RAG_HEX[overall]}14`, borderLeft: `3px solid ${RAG_HEX[overall]}` }}>
                <div className="px-2.5 py-[6px]">
                  <div className="font-cond text-[9px] tracking-[1.5px] uppercase font-bold mb-[2px]" style={{ color: RAG_HEX[overall] }}>
                    Why {RAG_WORD[overall]}
                  </div>
                  <div className={`text-gray-700 font-bold ${fit.why}`}>
                    {renderRich(report.whyLine, 'why')}
                  </div>
                </div>
              </div>
            )}
            {blocks.map((b, i) => b.type === 'ul' ? (
              <ul key={i} className={`text-gray-700 ${fit.body} ${fit.gap} list-disc pl-[15px] marker:text-[#005EB8]`}>
                {b.items.map((item, j) => (
                  <li key={j} className="pl-[1px]">{renderRich(item, `b${i}-${j}`)}</li>
                ))}
              </ul>
            ) : (
              // The gap belongs on each line, not on the block. Grouping
              // consecutive lines into one block is what bullets need, but
              // spacing the block alone ran every paragraph together.
              <Fragment key={i}>
                {b.lines.map((line, j) => (
                  <div key={j} className={`text-gray-700 ${fit.body} ${fit.gap}`}>
                    {renderRich(line, `p${i}-${j}`)}
                  </div>
                ))}
              </Fragment>
            ))}
            {!report.whyLine.trim() && blocks.length === 0 && (
              <div className="text-[10.5px] text-gray-400 italic">No summary written yet.</div>
            )}
          </div>
        </Card>

        {/* Cards render whatever the space budget allows, whole items only.
            A card whose every item was squeezed out vanishes with its header
            (a naturally empty card keeps its empty-state line - "no room" and
            "nothing logged" are different statements). overflow-hidden is the
            belt-and-braces: mid-measurement passes can never bleed over the
            milestones band below. */}
        <div ref={listsRef} className="flex flex-col gap-2 min-h-0 overflow-hidden">
          {(highlights.length === 0 || visibleCounts.highlights > 0) && (
          <Card className="shrink-0">
            <CardHead title="Highlights" icon={<Ico d={I.flag} stroke={GREEN} />} accent={GREEN}
              right={<div className="font-cond text-[9px] tracking-[1px] uppercase text-gray-500 font-bold">{highlights.length} Delivered</div>} />
            <div className="px-3 py-[6px]">
              <BulletList items={highlights.slice(0, visibleCounts.highlights)} colour={GREEN} empty="Nothing logged this month" />
              {hiddenCounts.highlights > 0 && (
                <div className="text-[9.5px] text-gray-400 italic mt-[3px]">+{hiddenCounts.highlights} not shown</div>
              )}
            </div>
          </Card>
          )}

          {(lowlights.length === 0 || visibleCounts.lowlights > 0) && (
          <Card className="shrink-0">
            <CardHead title="Lowlights" icon={<Ico d={I.warn} stroke={RED} />} accent={RED}
              right={<div className="font-cond text-[9px] tracking-[1px] uppercase text-gray-500 font-bold">{lowlights.length} Open</div>} />
            <div className="px-3 py-[6px]">
              <BulletList items={lowlights.slice(0, visibleCounts.lowlights)} colour={RED} empty="None" />
              {hiddenCounts.lowlights > 0 && (
                <div className="text-[9.5px] text-gray-400 italic mt-[3px]">+{hiddenCounts.lowlights} not shown</div>
              )}
            </div>
          </Card>
          )}

          {/* Straight from the project's risk register - grouped with the other
              bad news rather than under the narrative, since a reader scanning
              for problems looks down this column. */}
          {!report.hideRisks && (risks.length === 0 || visibleCounts.risks > 0) && (
          <Card className="shrink-0">
            <CardHead title="Open Risks" icon={<Ico d={I.shield} stroke={ORANGE} />} accent={ORANGE}
              right={
                <div className="font-cond text-[9px] tracking-[1px] uppercase text-gray-500 font-bold">
                  {risks.length === 0 ? 'None' : `${risks.length} open`}
                </div>
              } />
            <div className="px-3 py-[6px]">
              <RiskList risks={risks.slice(0, visibleCounts.risks)} hidden={hiddenCounts.risks} />
            </div>
          </Card>
          )}

          {asks.length > 0 && visibleCounts.supportNeeded > 0 && (
            <Card borderColor="rgba(248,136,6,0.35)" className="shrink-0">
              <CardHead title="Where we need your help" icon={<Ico d={I.bulb} stroke={ORANGE} />} accent={ORANGE} />
              <div className="px-3 py-[6px] flex flex-col gap-[4px]" style={{ background: 'rgba(248,136,6,0.06)' }}>
                {asks.slice(0, visibleCounts.supportNeeded).map(b => (
                  <div key={b.id} className="flex gap-1.5">
                    <span className="shrink-0 mt-[3px]"><Ico size={9} stroke={ORANGE} d={I.arrow} /></span>
                    <div className="text-[11px] text-gray-700 leading-[1.25]">{b.text}</div>
                  </div>
                ))}
                {hiddenCounts.supportNeeded > 0 && (
                  <div className="text-[9.5px] text-gray-500 italic">+{hiddenCounts.supportNeeded} not shown</div>
                )}
              </div>
            </Card>
          )}
        </div>
      </div>

      {/* Milestones */}
      <div className="px-8 pb-3 shrink-0">
        <Card>
          <CardHead title="Key Milestones" icon={<Ico d={I.cal} />}
            right={
              <div className="font-cond text-[9px] tracking-[1px] uppercase text-gray-500 font-bold">
                {achieved} Achieved{atRisk > 0 ? ` · ${atRisk} At Risk` : ''}
              </div>
            } />
          <Timeline milestones={s.milestones} reportDate={report.reportDate} />
        </Card>
      </div>
    </div>
  )
}
